import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import CONFIG from "../config";

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchStories = async (showLog = false) => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${CONFIG.API_URL}/api/stories`, { headers });

      const newStories = res.data;
      setStories(newStories);
      setLoading(false);

      const allVideosReady = newStories.every((s) => s.videoUrl);
      if (allVideosReady && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      } else if (showLog) {
        console.log("🔄 Checking for new videos...");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setMessage("❌ Failed to fetch stories.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
    intervalRef.current = setInterval(() => fetchStories(true), 10000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const getVideoSrc = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${CONFIG.API_URL}${url}`;
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)", padding: "40px 20px" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "48px" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "12px",
          background: "rgba(255,255,255,0.07)", backdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.12)", borderRadius: "50px",
          padding: "10px 24px", marginBottom: "20px"
        }}>
          <span style={{ fontSize: "22px" }}>🎬</span>
          <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase" }}>
            AI Stories
          </span>
        </div>
        <h1 style={{
          fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 800,
          background: "linear-gradient(135deg, #fff 0%, #a78bfa 50%, #f472b6 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          margin: 0, lineHeight: 1.1
        }}>
          Product Stories
        </h1>
        <p style={{ color: "rgba(255,255,255,0.5)", marginTop: "12px", fontSize: "16px" }}>
          AI-generated highlight reels for our products
        </p>
      </div>

      {/* Status messages */}
      {message && (
        <div style={{
          textAlign: "center", padding: "12px 24px",
          background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: "12px", color: "#fca5a5", maxWidth: "500px", margin: "0 auto 32px"
        }}>
          {message}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: "48px", height: "48px", borderRadius: "50%",
              border: "3px solid rgba(167,139,250,0.3)", borderTopColor: "#a78bfa",
              animation: "spin 1s linear infinite", margin: "0 auto 16px"
            }} />
            <p style={{ color: "rgba(255,255,255,0.5)" }}>Loading stories...</p>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : stories.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 20px" }}>
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>📭</div>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "18px" }}>No stories yet. Create a product with a story to get started!</p>
          <Link to="/admin/create-product" style={{
            display: "inline-block", marginTop: "24px", padding: "12px 28px",
            background: "linear-gradient(135deg, #7c3aed, #ec4899)",
            color: "#fff", borderRadius: "50px", textDecoration: "none",
            fontWeight: 600, fontSize: "15px"
          }}>
            ✨ Create Your First Story
          </Link>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "24px", maxWidth: "1200px", margin: "0 auto"
        }}>
          {stories.map((story, i) => (
            <div key={story.id} style={{
              background: "rgba(255,255,255,0.05)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "20px", overflow: "hidden",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              cursor: "pointer"
            }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow = "0 20px 60px rgba(124,58,237,0.3)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Video or placeholder */}
              {story.videoUrl ? (
                <video
                  style={{ width: "100%", height: "200px", objectFit: "cover", display: "block" }}
                  controls
                  preload="metadata"
                >
                  <source src={getVideoSrc(story.videoUrl)} type="video/mp4" />
                </video>
              ) : (
                <div style={{
                  height: "200px", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  background: `linear-gradient(135deg, hsl(${(i * 67) % 360}, 70%, 15%), hsl(${(i * 67 + 120) % 360}, 70%, 10%))`
                }}>
                  <div style={{ fontSize: "40px", marginBottom: "12px", animation: "pulse 2s infinite ease-in-out" }}>🎬</div>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textAlign: "center", padding: "0 20px" }}>
                    Video generating...
                  </p>
                  <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
                </div>
              )}

              {/* Card content */}
              <div style={{ padding: "20px" }}>
                <h3 style={{
                  color: "#fff", fontSize: "18px", fontWeight: 700,
                  margin: "0 0 8px", lineHeight: 1.3
                }}>
                  {story.title}
                </h3>
                <p style={{
                  color: "rgba(255,255,255,0.55)", fontSize: "14px",
                  lineHeight: 1.6, margin: "0 0 16px",
                  display: "-webkit-box", WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical", overflow: "hidden"
                }}>
                  {story.content}
                </p>

                {story.productId && (
                  <Link
                    to={`/shop/${story.productId}`}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "6px",
                      padding: "8px 16px", borderRadius: "50px",
                      background: "linear-gradient(135deg, #7c3aed, #ec4899)",
                      color: "#fff", textDecoration: "none",
                      fontSize: "13px", fontWeight: 600,
                      transition: "opacity 0.2s"
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                  >
                    🛒 View Product
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Stories;
